import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCms } from "../CmsContext";
import { PageHeader } from "../EditorChrome";
import { documentSitePath, timeAgo } from "../cmsUtils";

const GROUP_ORDER = ["pages", "services", "markets", "settings"];
const GROUP_LABELS = {
  pages: "Site pages",
  services: "Services",
  markets: "Markets",
  settings: "Site-wide settings",
};

export function PagesPage() {
  const { api, API_BASE } = useCms();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError("");
      const res = await api.get(`${API_BASE}/documents`);
      if (res && res.success) {
        setDocuments(res.documents || []);
      } else {
        setLoadError(res?.error || res?.message || "Could not reach the server");
      }
      setLoading(false);
    })();
  }, [api, API_BASE, reloadKey]);

  const grouped = useMemo(() => {
    const filtered = documents.filter(
      (d) =>
        !query ||
        (d.label || d.key).toLowerCase().includes(query.toLowerCase()) ||
        d.key.toLowerCase().includes(query.toLowerCase())
    );
    const groups = {};
    for (const doc of filtered) {
      const group = doc.group || "pages";
      if (!groups[group]) groups[group] = [];
      groups[group].push(doc);
    }
    return groups;
  }, [documents, query]);

  return (
    <div className="lw-page">
      <PageHeader
        title="Pages"
        subtitle="Structured copy and SEO for every page of the site."
        actions={
          <input
            className="lw-input lw-input--search"
            type="search"
            placeholder="Search pages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      />

      {loading ? (
        <p className="lw-empty">Loading pages…</p>
      ) : loadError ? (
        <div className="lw-empty">
          <p>
            Failed to load pages: <strong>{loadError}</strong>. Is the backend running?
          </p>
          <button
            type="button"
            className="lw-btn lw-btn--secondary lw-btn--sm"
            style={{ marginTop: 10 }}
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Retry
          </button>
        </div>
      ) : documents.length === 0 ? (
        <p className="lw-empty">No pages found — has the content been seeded?</p>
      ) : (
        GROUP_ORDER.filter((g) => grouped[g]?.length).map((group) => (
          <section key={group} className="lw-listsection">
            <h2 className="lw-listsection__title">{GROUP_LABELS[group] || group}</h2>
            <ul className="lw-rowlist">
              {grouped[group].map((doc) => (
                <li key={doc.key}>
                  <Link to={encodeURIComponent(doc.key)} className="lw-row">
                    <div className="lw-row__main">
                      <span className="lw-row__title">{doc.label || doc.key}</span>
                      <span className="lw-row__sub">{documentSitePath(doc.key)}</span>
                    </div>
                    <div className="lw-row__side">
                      {doc.hasUnpublishedChanges && (
                        <span className="lw-badge lw-badge--draft">Unpublished changes</span>
                      )}
                      {doc.updatedTime && (
                        <span className="lw-row__meta">
                          {doc.updatedBy?.fullName ? `${doc.updatedBy.fullName} · ` : ""}
                          {timeAgo(doc.updatedTime)}
                        </span>
                      )}
                      <span className="lw-row__chevron">›</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
